import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Collapse,
  Descriptions,
  List,
  message,
  Popconfirm,
  Result,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  HomeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { useCourseDetail } from "@/api/catalog";
import { AITutorFab } from "@/components/AITutorPanel";
import { ConversationPracticeFab } from "@/features/conversation/ConversationPracticeFab";
import {
  extractEnrollErrorMessage,
  useDropEnrollment,
  useEnroll,
  useEnrollmentFor,
} from "@/api/enrollment";
import { useCheckout, formatAmountVND, isFreeEnrollment } from "@/api/payment";
import { LessonRow } from "@/components/LessonListItem";
import { MyCourseGradesByTerm } from "@/components/MyCourseGradesByTerm";
import { CourseAssignments } from "@/components/CourseAssignments";
import { CourseForum } from "@/components/forum/CourseForum";

const { Title, Paragraph } = Typography;

export function CourseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const role = useAuthStore((s) => s.user?.role);
  const liveClassroomEnabled = useAuthStore((s) => s.user?.feature_flags?.live_classroom);
  const forumEnabled = useAuthStore((s) => s.user?.feature_flags?.forum);
  // Tab đang mở trên URL (`?tab=`) — link thông báo diễn đàn mở thẳng tab "Thảo luận".
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "overview";
  // Sprint 28 C4 — giảng viên/admin không enroll (BE chặn IsStudent → 403). Ẩn CTA,
  // hiện chế độ xem trước thay vì để bấm rồi báo lỗi.
  const isStaff = role === "instructor" || role === "tenant_admin" || role === "superadmin";
  const { data: course, isLoading, error } = useCourseDetail(slug);
  const { enrollment } = useEnrollmentFor(slug);
  // Khớp gate BE (content/views.py playback): chỉ ENROLLED/COMPLETED mới mở khoá bài
  // không-preview; "dropped" và staff (không enroll) vẫn khoá.
  const lessonsUnlocked = enrollment?.status === "enrolled" || enrollment?.status === "completed";
  const enroll = useEnroll();
  const drop = useDropEnrollment();
  const checkout = useCheckout();

  const handleEnroll = async () => {
    if (!slug) return;
    try {
      await enroll.mutateAsync(slug);
      message.success("Đăng ký thành công!");
    } catch (err: unknown) {
      message.error(extractEnrollErrorMessage(err, "Đăng ký thất bại. Vui lòng thử lại."), 6);
    }
  };

  const handleCheckout = async () => {
    if (!slug) return;
    try {
      const result = await checkout.mutateAsync(slug);
      if (isFreeEnrollment(result)) {
        // Free course → đã enroll trực tiếp
        message.success("Đăng ký thành công!");
      } else {
        // Paid → redirect tới payment_link (mock = /api/payment/mock/checkout/X/,
        // PayOS = https://pay.payos.vn/...)
        message.loading("Đang chuyển tới trang thanh toán...", 1);
        window.location.href = result.payment_link;
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        message.warning("Bạn đã đăng ký môn học này rồi.");
      } else {
        message.error(
          extractEnrollErrorMessage(err, "Không tạo được phiên thanh toán. Vui lòng thử lại."),
          6,
        );
      }
    }
  };

  const handleDrop = async () => {
    if (!slug) return;
    try {
      await drop.mutateAsync(slug);
      message.info("Đã bỏ môn học.");
    } catch {
      message.error("Bỏ môn học thất bại.");
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spin size="large" />
        <div style={{ marginTop: 12, color: "#7a8499" }}>Đang tải chi tiết môn học...</div>
      </div>
    );
  }
  if (error || !course) {
    return (
      <Result
        status="404"
        title="Không tìm thấy môn học"
        subTitle={String(error ?? "")}
        extra={
          <Link to="/my-courses">
            <a>← Về Môn học của tôi</a>
          </Link>
        }
      />
    );
  }

  const firstLessonSlug = [...course.lessons].sort((a, b) => a.order - b.order)[0]?.slug;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <Link to="/">
                <HomeOutlined />
              </Link>
            ),
          },
          { title: <Link to="/my-courses">Môn học của tôi</Link> },
          { title: course.name },
        ]}
      />

      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {!course.is_latest && (
            <Alert
              type="warning"
              showIcon
              message="Đây là phiên bản cũ của môn học"
              description="Đã có phiên bản mới hơn. Nội dung này giữ lại cho học sinh đã đăng ký bản này."
            />
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div>
              <Space size="small" wrap>
                <Tag color="blue">{course.code}</Tag>
                {course.tags.map((t) => (
                  <Tag key={t.id}>{t.name}</Tag>
                ))}
                {!course.is_published && <Tag color="orange">Bản nháp</Tag>}
                {course.version > 1 && <Tag color="purple">Phiên bản {course.version}</Tag>}
              </Space>
              <Title level={2} style={{ margin: "8px 0 0" }}>
                {course.name}
              </Title>
              {/* Việc B — vào trang ôn tập tổng hợp SRS theo khoá */}
              <Link to={`/courses/${slug}/srs`}>
                <Button
                  size="small"
                  icon={<BookOutlined />}
                  style={{ marginTop: 8 }}
                  data-testid="srs-review-link"
                >
                  Ôn tập tổng hợp
                </Button>
              </Link>
            </div>
            {/* ─── Enrollment CTA ─────────────────────────────────────── */}
            <div data-testid="enroll-cta">
              {isStaff && (
                <Tag icon={<BookOutlined />} color="blue">
                  Chế độ xem trước (giảng viên)
                </Tag>
              )}
              {!isStaff && !enrollment && course.effective_price_cents === null && (
                <Tag color="warning">Môn học chưa thiết lập học phí — liên hệ admin</Tag>
              )}
              {!isStaff &&
                !enrollment &&
                course.effective_price_cents !== null &&
                course.effective_price_cents > 0 && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<CreditCardOutlined />}
                    loading={checkout.isPending}
                    onClick={handleCheckout}
                  >
                    Thanh toán {formatAmountVND(course.effective_price_cents)}
                  </Button>
                )}
              {!isStaff && !enrollment && course.effective_price_cents === 0 && (
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  loading={enroll.isPending}
                  onClick={handleEnroll}
                >
                  Đăng ký miễn phí
                </Button>
              )}
              {!isStaff && enrollment?.status === "enrolled" && (
                <Space>
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    Đã đăng ký
                  </Tag>
                  <Popconfirm
                    title="Bỏ môn học?"
                    description="Bạn sẽ không xem được bài học (trừ bài preview) cho tới khi đăng ký lại."
                    okText="Bỏ học"
                    cancelText="Hủy"
                    onConfirm={handleDrop}
                  >
                    <Button size="small" danger loading={drop.isPending}>
                      Bỏ học
                    </Button>
                  </Popconfirm>
                </Space>
              )}
              {!isStaff && enrollment?.status === "completed" && (
                <Tag icon={<CheckCircleOutlined />} color="gold">
                  Đã hoàn thành
                </Tag>
              )}
              {!isStaff && enrollment?.status === "dropped" && (
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={enroll.isPending}
                  onClick={handleEnroll}
                >
                  Đăng ký lại
                </Button>
              )}
            </div>
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
            items={[
              {
                key: "overview",
                label: "Tổng quan",
                children: (
                  <Space direction="vertical" size="large" style={{ width: "100%" }}>
                    <Paragraph style={{ whiteSpace: "pre-line" }}>
                      {course.description || "Môn học này chưa có mô tả chi tiết."}
                    </Paragraph>
                    <Descriptions column={2} bordered size="small">
                      <Descriptions.Item label="Học kỳ">
                        {course.term_names.length > 0
                          ? course.term_names.join(", ")
                          : "Chưa gắn kỳ học"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Số tín chỉ">
                        <ClockCircleOutlined /> {course.credits} TC
                      </Descriptions.Item>
                      <Descriptions.Item label="Số bài học" span={2}>
                        <BookOutlined /> {course.lessons.length} bài
                      </Descriptions.Item>
                    </Descriptions>
                  </Space>
                ),
              },
              {
                key: "lessons",
                // Môn chia Chương chỉ hiện bài thuộc chương (bài ngoài chương — vd bản cũ bị
                // gói nội dung thay — không hiện) → số đếm khớp danh sách.
                label: `Bài học (${
                  course.modules && course.modules.length > 0
                    ? course.modules.reduce((n, m) => n + m.lessons.length, 0)
                    : course.lessons.length
                })`,
                children:
                  course.lessons.length === 0 ? (
                    <Alert type="info" showIcon message="Môn học chưa có bài học nào." />
                  ) : course.modules && course.modules.length > 0 ? (
                    // Sprint 28 — render theo Chương → Bài (course publish từ AI Designer)
                    <Collapse
                      defaultActiveKey={course.modules.map((m) => String(m.id))}
                      items={[...course.modules]
                        .sort((a, b) => a.order - b.order)
                        .map((m) => ({
                          key: String(m.id),
                          label: `Chương ${m.order}: ${m.title} (${m.lessons.length} bài)`,
                          children: (
                            <List
                              dataSource={[...m.lessons].sort((a, b) => a.order - b.order)}
                              renderItem={(lesson) => (
                                <LessonRow lesson={lesson} unlocked={lessonsUnlocked} />
                              )}
                            />
                          ),
                        }))}
                    />
                  ) : (
                    // Fallback: course phẳng (legacy / không dùng Chương)
                    <List
                      dataSource={[...course.lessons].sort((a, b) => a.order - b.order)}
                      renderItem={(lesson) => (
                        <LessonRow lesson={lesson} unlocked={lessonsUnlocked} />
                      )}
                    />
                  ),
              },
              // Bài tập của môn, xếp theo bài học "nên làm sau" (2026-10-01).
              ...(!isStaff &&
              slug &&
              (enrollment?.status === "enrolled" || enrollment?.status === "completed")
                ? [
                    {
                      key: "assignments",
                      label: "Bài tập",
                      children: <CourseAssignments courseSlug={slug} />,
                    },
                  ]
                : []),
              // Điểm của chính sinh viên, tách theo từng kỳ đã đăng ký (2026-10-01).
              ...(!isStaff &&
              slug &&
              (enrollment?.status === "enrolled" || enrollment?.status === "completed")
                ? [
                    {
                      key: "grades",
                      label: "Điểm của tôi",
                      children: <MyCourseGradesByTerm courseSlug={slug} />,
                    },
                  ]
                : []),
              {
                key: "quizzes",
                label: "Bài kiểm tra",
                children: (
                  <Alert
                    type="info"
                    showIcon
                    message={
                      <Link to={`/courses/${slug}/quizzes`}>
                        Xem danh sách bài kiểm tra của khóa →
                      </Link>
                    }
                    description="Đăng ký môn học để có quyền làm bài. Mỗi bài có thời gian giới hạn + giám sát webcam."
                  />
                ),
              },
              // Plan 2026-10-03 — diễn đàn: SV đã ghi danh (kể cả đã học xong/bỏ môn — chỉ đọc).
              ...(forumEnabled && !isStaff && slug && enrollment
                ? [
                    {
                      key: "forum",
                      label: "Thảo luận",
                      children: <CourseForum courseSlug={slug} />,
                    },
                  ]
                : []),
              // Sprint 12 B5 — ẩn hoàn toàn khi LIVE_CLASSROOM_ENABLED=False,
              // tránh học viên bấm vào rồi gặp 503 khó hiểu.
              ...(liveClassroomEnabled
                ? [
                    {
                      key: "live",
                      label: "Lớp học live",
                      children: (
                        <Alert
                          type="info"
                          showIcon
                          message={
                            <Link to={`/courses/${slug}/live`}>
                              Xem lịch lớp học live của khóa →
                            </Link>
                          }
                          description="Giảng viên lên lịch buổi live — vào phòng khi buổi đang diễn ra."
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Space>
      </Card>

      {/* AI Tutor trên trang môn (2026-10-03) — conversation gắn bài đầu tiên của môn
          → RAG trong phạm vi nội dung môn. Chỉ khi đã mở khoá bài (enrolled/completed)
          hoặc staff xem trước. */}
      {(lessonsUnlocked || isStaff) && firstLessonSlug && (
        <AITutorFab
          lessonSlug={firstLessonSlug}
          title={course.name}
          tooltip="Hỏi AI Tutor về môn này"
        />
      )}
      {/* Luyện giao tiếp với AI (Gemini Live) — chỉ môn ngôn ngữ. */}
      {(lessonsUnlocked || isStaff) && course.speaking_language && slug && (
        <ConversationPracticeFab courseSlug={slug} language={course.speaking_language} />
      )}
    </div>
  );
}

export default CourseDetail;
